import { APITypesV1 } from "@cur8/api-client";
import {
  Button,
  Icon16AlertTriangle,
  Icon16Checkmark,
  Toggle,
  Typography,
} from "@cur8/maneki";
import { Measurement } from "@cur8/rich-entity";
import { UserURI } from "@cur8/uri";
import { UseMutationResult } from "@tanstack/react-query";
import { usePreviousMeasurementQuery } from "hooks/queries/usePreviousMeasurementQuery";
import { formatDate } from "lib/formatDateTime";
import { useCallback, useState } from "react";
import {
  SeverityLevel,
  useAppInsights,
} from "render/context/AppInsightsContext";
import { useMSAL } from "render/context/MSALContext";
import {
  CONCIERGE_GENERATOR,
  MetricConfig,
  getImperialConversion,
} from "./measurementConfig";
import { CONVERSION_DEFS, isOutOfRange, round1 } from "./measurementConversion";
import { formatMeasurementValue, formatUnit } from "./measurementFormat";
import { isDiastolicAboveSystolic } from "./measurementPlausibility";
import styles from "./styles.module.sass";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type StoreMutation = UseMutationResult<
  APITypesV1.Measurement,
  unknown,
  APITypesV1.CreateMeasurementRequest
>;

interface MeasurementEntryProps {
  config: MetricConfig;
  patientId: string;
  visitId: string;
  isAutoImported: boolean;
  existing: Measurement | undefined;
  riskRanges: APITypesV1.RiskRange[] | undefined;
  countryCode: string | undefined;
  // Owned by MeasurementsBottomSheet so the choice survives closing this form.
  showImperial: boolean;
  onToggleImperial: (showImperial: boolean) => void;
  // Off until the retest alerts flag is on.
  rejectImpossible?: boolean;
  storeMeasurement: StoreMutation;
  onBack: () => void;
  onDone: () => void;
}

export function MeasurementEntry({
  config,
  patientId,
  visitId,
  isAutoImported,
  existing,
  riskRanges,
  countryCode,
  showImperial,
  onToggleImperial,
  rejectImpossible,
  storeMeasurement,
  onBack,
  onDone,
}: MeasurementEntryProps) {
  const { auth } = useMSAL();
  const appInsights = useAppInsights();

  // Imperial conversion available for this metric + country (null = metric only)
  const imperialConv = getImperialConversion(config, countryCode);

  // The active conversion def (null when in metric mode)
  const convDef =
    showImperial && imperialConv ? CONVERSION_DEFS[imperialConv] : null;

  const { data: previousMeasurement, isError: previousValueFailedToLoad } =
    usePreviousMeasurementQuery({
      patientId,
      metricName: config.metricName,
      excludeVisitId: visitId,
    });
  // Formatted in whichever unit is currently selected, so it matches the
  // live input's unit rather than always showing the config's base unit.
  const previousValue = formatMeasurementValue(
    config,
    previousMeasurement,
    convDef
  );

  // Initialize display values from an existing measurement (always SI from API)
  const [values, setValues] = useState<string[]>(() => {
    if (convDef) {
      const siDim = existing?.dimensions.find(
        (d) => d.name === config.dimensions[0].name
      );
      if (!siDim) {
        return convDef.fields.map(() => "");
      }
      return convDef.toDisplay(siDim.value).map(String);
    }
    return config.dimensions.map((dimConfig) => {
      const dim = existing?.dimensions.find((d) => d.name === dimConfig.name);
      return dim != null ? String(dim.value) : "";
    });
  });

  const [verifiedRisk, setVerifiedRisk] = useState(false);

  // Toggle between metric and imperial, converting current input values
  const handleToggleImperial = useCallback(() => {
    if (!imperialConv) {
      return;
    }
    const def = CONVERSION_DEFS[imperialConv];
    const targetImperial = !showImperial;

    if (targetImperial) {
      const si = parseFloat(values[0]);
      setValues(
        !isNaN(si) ? def.toDisplay(si).map(String) : def.fields.map(() => "")
      );
    } else {
      const parsed = values.map(parseFloat);
      setValues(
        parsed.every((v) => !isNaN(v))
          ? [String(def.toSI(parsed))]
          : config.dimensions.map(() => "")
      );
    }

    onToggleImperial(targetImperial);
  }, [showImperial, imperialConv, values, config.dimensions, onToggleImperial]);

  // SI values for risk range checks and the API payload
  const siValues: Array<number | null> = convDef
    ? (() => {
        const parsed = values.map(parseFloat);
        if (parsed.some(isNaN)) {
          return config.dimensions.map(() => null);
        }
        return [convDef.toSI(parsed)];
      })()
    : values.map((v) => (v === "" ? null : parseFloat(v)));

  const allFilled = siValues.every((v) => v !== null && !isNaN(v as number));

  const riskWarnings: string[] = [];
  if (riskRanges && allFilled) {
    config.dimensions.forEach((dimConfig, i) => {
      const val = siValues[i];
      if (val == null) {
        return;
      }
      const range = riskRanges.find((r) => r.dimensionName === dimConfig.name);
      if (!range) {
        return;
      }
      if (isOutOfRange(val, range)) {
        riskWarnings.push(
          `${dimConfig.name}: ${val} ${formatUnit(dimConfig.unit)} is outside normal range.`
        );
      }
    });
  }

  const hasRiskWarning = riskWarnings.length > 0;

  // A hard block, not a risk warning: the verify toggle may wave through a
  // risky value but not an arithmetically invalid one.
  const impossibleError = (() => {
    if (rejectImpossible !== true) {
      return undefined;
    }
    const systolicIndex = config.dimensions.findIndex(
      (d) => d.name === "systolic"
    );
    const diastolicIndex = config.dimensions.findIndex(
      (d) => d.name === "diastolic"
    );
    if (systolicIndex < 0 || diastolicIndex < 0) {
      return undefined;
    }
    return isDiastolicAboveSystolic(
      siValues[systolicIndex],
      siValues[diastolicIndex]
    )
      ? "Diastolic measurement must be lower than systolic measurement. Check and re-enter the reading."
      : undefined;
  })();

  const canSave =
    !!auth &&
    allFilled &&
    !impossibleError &&
    (!hasRiskWarning || verifiedRisk);

  const handleSave = useCallback(() => {
    if (!canSave || !auth) {
      appInsights.trackTrace({
        message: "Measurement save blocked on press",
        severityLevel: SeverityLevel.Warning,
        properties: {
          metricName: config.metricName,
          visitId,
          canSave,
          hasAuth: !!auth,
          allFilled,
          hasRiskWarning,
          verifiedRisk,
          impossibleError: impossibleError ?? null,
        },
      });
      return;
    }

    appInsights.trackTrace({
      message: "Measurement save attempted",
      severityLevel: SeverityLevel.Information,
      properties: {
        metricName: config.metricName,
        visitId,
        hasRiskWarning,
        verifiedRisk,
      },
    });

    const dimensions: APITypesV1.CreateMeasurementDimension[] =
      config.dimensions.map((dimConfig, i) => ({
        name: dimConfig.name,
        value: siValues[i] as number,
        unit: dimConfig.unit,
      }));

    storeMeasurement.mutate(
      {
        sourceUri: new UserURI(auth.tenantId, auth.uniqueId).toString(),
        generator: CONCIERGE_GENERATOR,
        metricName: config.metricName,
        timestampStart: new Date().toISOString(),
        type: null,
        dimensions,
        visitId,
      },
      { onSuccess: onDone }
    );
  }, [
    canSave,
    auth,
    visitId,
    config,
    siValues,
    storeMeasurement,
    onDone,
    appInsights,
    allFilled,
    hasRiskWarning,
    verifiedRisk,
    impossibleError,
  ]);

  // Display fields: either the imperial conversion fields or the metric dimensions
  const displayFields: ReadonlyArray<{ name: string; unit: string }> = convDef
    ? convDef.fields
    : config.dimensions;

  // Convertible metrics show both units at once: the active unit is editable,
  // the other is derived read-only from the same SI value (single dimension).
  const metricLabel = formatUnit(config.dimensions[0].unit);
  const imperialLabel = imperialConv ? CONVERSION_DEFS[imperialConv].label : "";
  const activeLabel = convDef ? imperialLabel : metricLabel;
  const derivedLabel = convDef ? metricLabel : imperialLabel;
  const derivedFields = convDef
    ? config.dimensions
    : imperialConv
      ? CONVERSION_DEFS[imperialConv].fields
      : [];
  const derivedSi = siValues[0];
  const derivedValues: string[] =
    derivedSi == null || isNaN(derivedSi)
      ? derivedFields.map(() => "")
      : convDef
        ? [String(round1(derivedSi))]
        : imperialConv
          ? CONVERSION_DEFS[imperialConv].toDisplay(derivedSi).map(String)
          : [];

  const updateValue = (i: number, val: string) =>
    setValues((prev) => {
      const next = [...prev];
      next[i] = val;
      return next;
    });

  return (
    <div className={styles.entry}>
      <div className={styles.entryHeader}>
        <Typography variant="display-s">{config.label}</Typography>
        {imperialConv && (
          <Button
            variant="secondary"
            size="small"
            onPress={handleToggleImperial}
            icon={<SwitchIcon />}
          >
            Switch to {derivedLabel}
          </Button>
        )}
      </div>

      {imperialConv ? (
        <div className={styles.conversionRow}>
          <UnitColumn
            label={activeLabel}
            fields={displayFields}
            values={values}
            onChange={updateValue}
          />
          <div className={styles.conversionEquals} aria-hidden="true">
            <span className={styles.equalsSpacer} />
            <div className={styles.equalsSign}>=</div>
          </div>
          <UnitColumn
            label={derivedLabel}
            fields={derivedFields}
            values={derivedValues}
            readOnly
          />
        </div>
      ) : (
        displayFields.map((field, i) => (
          <DimensionInput
            key={field.name}
            name={field.name}
            unit={field.unit}
            value={values[i]}
            onChange={(val) => updateValue(i, val)}
          />
        ))
      )}

      {(previousValue || previousValueFailedToLoad) && (
        <div className={styles.previousValue}>
          {previousValueFailedToLoad ? (
            <Typography variant="body-s" color="subtle">
              There is no previous value for this measurement
            </Typography>
          ) : (
            <>
              <Typography variant="body-s">
                Previous value: {previousValue}
              </Typography>
              {previousMeasurement && (
                <Typography variant="body-s" color="subtle">
                  {formatDate(previousMeasurement.timestampStart, countryCode)}
                </Typography>
              )}
            </>
          )}
        </div>
      )}

      {impossibleError && (
        <div className={styles.riskWarning}>
          <div className={styles.riskWarningHeader}>
            <Icon16AlertTriangle />
            <Typography variant="label-s">Impossible reading</Typography>
          </div>
          <Typography variant="body-s">{impossibleError}</Typography>
        </div>
      )}

      {/* Hidden while impossible: an unsaveable value must not also offer a
          toggle that reads as a way to save it. */}
      {!impossibleError && hasRiskWarning && (
        <div className={styles.riskWarning}>
          <div className={styles.riskWarningHeader}>
            <Icon16AlertTriangle />
            {/* color is load-bearing: Typography emits no data-color without
                it, leaving the title inheriting the header's amber. */}
            <Typography variant="label-s" color="default">
              Value outside normal range
            </Typography>
          </div>
          {riskWarnings.map((w) => (
            <Typography key={w} variant="body-s">
              {w}
            </Typography>
          ))}
          <div className={styles.verifyRow}>
            <Toggle isSelected={verifiedRisk} onChange={setVerifiedRisk} />
            <Typography variant="body-s">
              I've verified this value is correct.
            </Typography>
          </div>
        </div>
      )}

      {isAutoImported && !existing && (
        <div className={styles.waitingForDevice}>
          <Typography variant="body-s">
            Waiting for device to send measurement
          </Typography>
        </div>
      )}

      <div className={styles.buttonRow}>
        <Button variant="secondary" onPress={onBack} fillContainer>
          Back
        </Button>
        <Button
          variant="primary"
          onPress={handleSave}
          fillContainer
          state={
            !canSave || storeMeasurement.isPending ? "disabled" : undefined
          }
        >
          {storeMeasurement.isPending
            ? "Saving..."
            : isAutoImported
              ? "Manually overwrite"
              : "Save"}
        </Button>
      </div>

      {storeMeasurement.isError && (
        <Typography variant="body-s" color="warning">
          Failed to save. Please try again.
        </Typography>
      )}

      {storeMeasurement.isSuccess && (
        <div className={styles.successRow}>
          <Icon16Checkmark />
          <Typography variant="body-s">Saved</Typography>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SwitchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 5.5h9M8.5 3 11 5.5 8.5 8M14 10.5H5M7.5 8 5 10.5 7.5 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface UnitColumnProps {
  label: string;
  fields: ReadonlyArray<{ name: string; unit: string }>;
  values: string[];
  onChange?: (index: number, value: string) => void;
  readOnly?: boolean;
}

// One side of the conversion row: a unit label over its field(s). The active
// unit is editable; the derived unit is one grouped read-only container whose
// segments ("auto" until filled) mirror each sub-unit of the conversion.
function UnitColumn({
  label,
  fields,
  values,
  onChange,
  readOnly,
}: UnitColumnProps) {
  return (
    <div className={styles.unitColumn}>
      <Typography variant="label-s">{label}</Typography>
      {readOnly ? (
        <div className={styles.derivedValue}>
          {values[0] ? (
            fields.map((field, i) => (
              <span key={field.name} className={styles.derivedPart}>
                {values[i]}
                {fields.length > 1 && (
                  <span className={styles.derivedUnit}>
                    {" "}
                    {formatUnit(field.unit)}
                  </span>
                )}
              </span>
            ))
          ) : (
            <span className={styles.derivedPlaceholder}>auto</span>
          )}
        </div>
      ) : (
        <div className={styles.unitFields}>
          {fields.map((field, i) => (
            <input
              key={field.name}
              className={styles.input}
              type="number"
              inputMode="decimal"
              value={values[i] ?? ""}
              onChange={(e) => onChange?.(i, e.target.value)}
              placeholder={`Enter ${formatUnit(field.unit)}`}
              aria-label={`Enter ${formatUnit(field.unit)}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface DimensionInputProps {
  name: string;
  unit: string;
  value: string;
  onChange: (val: string) => void;
}

function DimensionInput({ name, unit, value, onChange }: DimensionInputProps) {
  return (
    <div className={styles.dimensionInput}>
      <label htmlFor={name} className={styles.dimensionLabel}>
        <Typography variant="label-s">{name}</Typography>
        <Typography variant="body-s" color="subtle">
          {formatUnit(unit)}
        </Typography>
      </label>
      <input
        id={name}
        className={styles.input}
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${formatUnit(unit)}`}
      />
    </div>
  );
}
